import type { ScoringConfig } from '../config/scoring.config';

/** Pickups counted per kind for the HUD – what the runner collected on the track (no quiz or dozer bonus). */
export interface PickupCounts {
  /** Bottle value of the collected items (bottle 1, crate 5, golden bottle and toolbox 10). */
  readonly bottles: number;
  /** Bonus helmets (extra life). */
  readonly helmets: number;
  /** Beer crates (turbo). */
  readonly crates: number;
}

export type PickupKind = keyof PickupCounts;

export interface ScoreSnapshot {
  readonly distance: number;
  /** Bottles of the score formula: pickups plus the quiz and dozer bonuses. */
  readonly bottles: number;
  readonly pickups: PickupCounts;
  /** Bottles collected towards the next extra life (0 … bottlesPerExtraLife-1). */
  readonly bottleProgress: number;
  /** Bottles needed per extra life in this run (config × character perk). */
  readonly bottlesPerExtraLife: number;
  readonly extraLives: number;
  readonly score: number;
}

/** Per-run changes of the economy – the character perks (CharacterPerks in character.config). */
export interface ScoreModifiers {
  /** Extra lives on top of ScoringConfig.startExtraLives. */
  readonly startExtraLives: number;
  /** Multiplies ScoringConfig.bottlesPerExtraLife (1 = unchanged). */
  readonly bottlesPerExtraLifeFactor: number;
}

export const NO_SCORE_MODIFIERS: ScoreModifiers = { startExtraLives: 0, bottlesPerExtraLifeFactor: 1 };

/**
 * Run statistics and the bottle → extra life economy.
 * The score formula lives here only (server-side validation will mirror it).
 */
export class ScoreManager {
  private distance = 0;
  private bottles = 0;
  private bottleProgress = 0;
  private extraLives = 0;
  private bottlesPerLife = 1;
  private pickups: Record<PickupKind, number> = { bottles: 0, helmets: 0, crates: 0 };

  constructor(private readonly config: ScoringConfig) {
    this.reset();
  }

  /** Starts a new run; the modifiers (character perks) apply until the next reset. */
  reset(modifiers: ScoreModifiers = NO_SCORE_MODIFIERS): void {
    this.distance = 0;
    this.bottles = 0;
    this.pickups = { bottles: 0, helmets: 0, crates: 0 };
    this.bottleProgress = 0;
    this.bottlesPerLife = Math.max(1, Math.round(this.config.bottlesPerExtraLife * modifiers.bottlesPerExtraLifeFactor));
    this.extraLives = Math.max(0, Math.min(this.config.startExtraLives + modifiers.startExtraLives, this.config.maxExtraLives));
  }

  /** Bottles needed per extra life in this run. */
  get bottlesPerExtraLife(): number {
    return this.bottlesPerLife;
  }

  setDistance(distance: number): void {
    this.distance = Math.max(this.distance, distance);
  }

  /**
   * Adds bottles; returns the number of extra lives gained – at most `maxLives` (quiz bonus): the bottles past that
   * count for the score only.
   */
  addBottles(amount: number, maxLives = Number.POSITIVE_INFINITY): number {
    if (amount <= 0) return 0;
    this.bottles += amount;
    this.bottleProgress += amount;
    let gained = 0;
    while (this.bottleProgress >= this.bottlesPerLife) {
      this.bottleProgress -= this.bottlesPerLife;
      if (gained < maxLives && this.addExtraLife()) gained++;
    }
    return gained;
  }

  /**
   * Takes bottles off (penalty of the golden bottle: a wrong answer, a mini game without a hit) – off the score AND off
   * the bottle counter in the HUD, the points follow (user 2026-10-04: "real nicht abgezogen … oben im Counter"). Never
   * below zero, never an extra life – the progress towards the next one shrinks with it. Returns the bottles taken.
   */
  removeBottles(amount: number): number {
    if (amount <= 0) return 0;
    const taken = Math.min(this.bottles, amount);
    this.bottles -= taken;
    this.bottleProgress = Math.max(0, this.bottleProgress - taken);
    this.pickups.bottles = Math.max(0, this.pickups.bottles - taken);
    return taken;
  }

  /**
   * Bonus bottles of the golden bottle (quiz, mini games): like addBottles, and they show on the bottle counter too –
   * the counterpart of removeBottles, so a won and a lost bonus both move the number the player sees.
   */
  addBonusBottles(amount: number, maxLives = Number.POSITIVE_INFINITY): number {
    if (amount <= 0) return 0;
    const lives = this.addBottles(amount, maxLives);
    this.pickups.bottles += amount;
    return lives;
  }

  /** Counts a collected item for the HUD (the score itself comes from addBottles). */
  recordPickup(kind: PickupKind, amount = 1): void {
    if (amount > 0) this.pickups[kind] += amount;
  }

  /** Returns false if the maximum is already reached. */
  addExtraLife(): boolean {
    if (this.extraLives >= this.config.maxExtraLives) return false;
    this.extraLives++;
    return true;
  }

  /** Spends one extra life. Returns false if none was left. */
  consumeExtraLife(): boolean {
    if (this.extraLives <= 0) return false;
    this.extraLives--;
    return true;
  }

  get score(): number {
    return Math.floor(this.distance * this.config.pointsPerMeter + this.bottles * this.config.pointsPerBottle);
  }

  snapshot(): ScoreSnapshot {
    return {
      distance: this.distance,
      bottles: this.bottles,
      pickups: { ...this.pickups },
      bottleProgress: this.bottleProgress,
      bottlesPerExtraLife: this.bottlesPerLife,
      extraLives: this.extraLives,
      score: this.score,
    };
  }
}
